import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Label } from "../../misc/Label";
import { Button } from "../../misc/Button";
import { ArrowLeft, Check, Rocket, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useUploadNavigationGuard } from "../../../hooks/useUploadNavigationGuard";
import { publishAutopilotEnabled } from "../../../hooks/useAutopilotEnabled";
import { cn } from "../../../utils/classMerger";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import LoadingScreen from "../../../screens/LoadingScreen";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import {
    AUTOPILOT_BUSY_POLL_MS,
    AUTOPILOT_OFF_BUSY_TOOLTIP,
    AutopilotReconcileResult,
    AutopilotResponse,
    AutopilotSettings,
    StagedAutopilotSettings as StagedSettings,
} from "../../../types/autopilot";
import AutopilotSettingsFields, {
    ALL_AUTOPILOT_CHANNELS as ALL_CHANNELS,
} from "./AutopilotSettingsFields";

const pickStaged = (settings: AutopilotSettings): StagedSettings => ({
    enabled: settings.enabled,
    mode: settings.mode,
    channels: settings.channels || [],
    openHouseSync: settings.openHouseSync,
    frontOfProperty: settings.frontOfProperty,
    floorPlanPosts: settings.floorPlanPosts,
    ctaPlacement: settings.ctaPlacement,
    listingPriority: settings.listingPriority,
    commercialUsePrice: settings.commercialUsePrice,
});

// What a completed save reports. Built by whichever transition ran, so
// persistStaged is the only place that notifies. A `title` means the outcome is
// big enough to hold the screen — turning Autopilot on or off moves campaigns
// and listings in bulk, and a toast that auto-closes is too easy to miss.
interface SaveOutcome {
    title?: string;
    message: string;
    error?: boolean;
}

const sameChannels = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((channel) => b.includes(channel));

const Autopilot: React.FC = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);

    const [data, setData] = useState<AutopilotResponse | null>(null);
    const [staged, setStaged] = useState<StagedSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmAction, setConfirmAction] = useState<
        "enable" | "disable" | null
    >(null);
    // Covers the WHOLE save, not just the PATCH: reconcile and disable-cleanup
    // are the slow legs, and dropping the loader before them left the user on a
    // page that looked idle until a toast appeared minutes later.
    const [busyMessage, setBusyMessage] = useState<string | null>(null);
    // Save pressed while Autopilot is off — the choice modal is open.
    const [pendingSave, setPendingSave] = useState(false);
    // Result of an on/off transition, held on screen until dismissed.
    const [outcomeModal, setOutcomeModal] = useState<SaveOutcome | null>(null);
    const [leaveOverlay, setLeaveOverlay] = useState(false);
    // Set when the user confirms "Leave" on a browser back/forward attempt: it drops the
    // guard (count → 0) so the pending navigation can complete (see the effect below).
    const [forceLeave, setForceLeave] = useState(false);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const headers = await getAuthHeader();
            const response = await axios.get(`${backendUrl}/settings/autopilot`, {
                headers,
            });
            setData(response.data);
            setStaged(pickStaged(response.data.settings));
        } catch (error) {
            console.error("Failed to fetch Autopilot settings:", error);
            toast.error(
                <CustomToast
                    message="Failed to load Auto Pilot settings"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    }, [backendUrl]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const autopilotBusy = data?.settings.enabled === true && data.busy === true;

    // Only `busy` is refreshed here - the staged edits must survive the poll.
    useEffect(() => {
        if (!autopilotBusy) return;
        const id = window.setInterval(async () => {
            try {
                const headers = await getAuthHeader();
                const response = await axios.get<AutopilotResponse>(
                    `${backendUrl}/settings/autopilot`,
                    { headers }
                );
                setData((prev) =>
                    prev ? { ...prev, busy: response.data.busy } : prev
                );
            } catch (error) {
                console.error("Failed to re-check Auto Pilot state:", error);
            }
        }, AUTOPILOT_BUSY_POLL_MS);
        return () => window.clearInterval(id);
    }, [autopilotBusy, backendUrl]);

    const isDirty =
        !loading &&
        !!data &&
        !!staged &&
        (staged.enabled !== data.settings.enabled ||
            staged.mode !== data.settings.mode ||
            staged.openHouseSync !== data.settings.openHouseSync ||
            staged.frontOfProperty !== data.settings.frontOfProperty ||
            staged.floorPlanPosts !== data.settings.floorPlanPosts ||
            staged.ctaPlacement !== data.settings.ctaPlacement ||
            staged.listingPriority !== data.settings.listingPriority ||
            staged.commercialUsePrice !== data.settings.commercialUsePrice ||
            !sameChannels(staged.channels, data.settings.channels || []));

    // Guard ALL in-app navigation away from this screen while there are unsaved changes
    // (same pattern as MlsDetails): the shared hook intercepts programmatic navigate()
    // AND the browser back/forward buttons; `beforeunload` covers refresh / tab close.
    //
    // NOTE: the hook arms its browser-back "sentinel" only when its effect re-runs with
    // count > 0, and that effect keys on `onNavigationAttempt` — so this callback MUST
    // change when `isDirty` flips (hence the [isDirty] dep). A stable ([]) callback would
    // leave the browser-back button unguarded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onNavigationAttempt = useCallback(() => setLeaveOverlay(true), [isDirty]);
    const { pendingNavigation, confirmNavigation, cancelNavigation } =
        useUploadNavigationGuard({
            uploadCount: isDirty && !forceLeave ? 1 : 0,
            onNavigationAttempt,
        });

    // Browser back/forward doesn't carry a target, so confirmNavigation() can't complete
    // it. When the user confirms "Leave" on a back-button attempt we drop the guard
    // (forceLeave → count 0) and then navigate to the parent Settings page here.
    useEffect(() => {
        if (forceLeave) {
            navigate("/settings");
        }
    }, [forceLeave, navigate]);

    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent): void => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const patchSettings = async (
        patch: Partial<AutopilotSettings>
    ): Promise<AutopilotResponse | null> => {
        try {
            setSaving(true);
            const headers = await getAuthHeader();
            const response = await axios.patch(
                `${backendUrl}/settings/autopilot`,
                patch,
                { headers }
            );
            setData(response.data);
            publishAutopilotEnabled(
                response.data?.settings?.enabled === true
            );
            return response.data;
        } catch (error) {
            console.error("Failed to update Autopilot settings:", error);
            // A 423 proves setup is in flight: disable the switch now rather
            // than only after the next page load.
            if (axios.isAxiosError(error) && error.response?.status === 423) {
                setData((prev) => (prev ? { ...prev, busy: true } : prev));
            }
            const message =
                axios.isAxiosError(error) && error.response?.data?.message
                    ? String(error.response.data.message)
                    : "Failed to update Auto Pilot settings";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 4000,
            });
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Kick the reconciler and describe what it did. Returns the line for the
    // save toast rather than raising its own — persistStaged is the only place
    // that notifies, so every save reports exactly once.
    const runReconcile = async (): Promise<SaveOutcome> => {
        try {
            const headers = await getAuthHeader();
            const result = await axios.post<AutopilotReconcileResult>(
                `${backendUrl}/autopilot/reconcile`,
                {},
                { headers }
            );
            // MB-1420: enabling from the MLS wizard lands here while the
            // import is still processing, so the reconciler defers and every
            // count is 0. Saying "new listings will be marketed as they sync
            // in" would read as "your current listings were skipped".
            if (result.data?.deferred || result.data?.lockBusy) {
                return {
                    title: "Auto Pilot is on",
                    message:
                        "Campaigns for your current listings will be created shortly.",
                };
            }
            const created = result.data?.created ?? 0;
            // Recreated campaigns are counted in `created` too, so the fresh
            // listings are the remainder. An older api only sends `converted`.
            const replaced =
                result.data?.replaced ?? result.data?.converted ?? 0;
            const fresh = Math.max(0, created - replaced);
            const discarded = result.data?.discardedPosts ?? 0;
            const parts: string[] = [];
            if (fresh > 0) {
                parts.push(
                    `started marketing ${fresh} listing${
                        fresh === 1 ? "" : "s"
                    }`
                );
            }
            if (replaced > 0) {
                parts.push(
                    `replaced ${replaced} existing campaign${
                        replaced === 1 ? "" : "s"
                    } with Auto Pilot campaigns`
                );
            }
            if (result.data?.promotionAdopted) {
                parts.push("took over your Neighborhood Expert promotion");
            }
            return {
                title: "Auto Pilot is on",
                message: `${
                    parts.length > 0
                        ? `Auto Pilot ${parts.join(" and ")}.`
                        : "New listings will be marketed automatically as they sync in."
                }${
                    discarded > 0
                        ? ` ${discarded} unpublished post${
                              discarded === 1 ? "" : "s"
                          } ${
                              discarded === 1 ? "was" : "were"
                          } discarded and ${
                              discarded === 1 ? "is" : "are"
                          } being regenerated on Auto Pilot's schedule; published posts are unchanged.`
                        : ""
                }`,
            };
        } catch (error) {
            console.error("Autopilot reconcile failed:", error);
            return {
                title: "Auto Pilot is on",
                message:
                    "Campaigns for your current listings will be created shortly.",
            };
        }
    };

    const runDisableCleanup = async (): Promise<SaveOutcome> => {
        try {
            const headers = await getAuthHeader();
            const result = await axios.post(
                `${backendUrl}/autopilot/disable-cleanup`,
                {},
                { headers }
            );
            const removed = result.data?.removed ?? 0;
            // `removed` counts listing campaigns AND the promotion campaign,
            // so calling every unit a "listing" was wrong whenever a
            // Neighborhood Expert promotion was part of the cleanup.
            return {
                title: "Auto Pilot is off",
                message: `${removed} item${
                    removed === 1 ? "" : "s"
                } deleted - the listings Auto Pilot managed and any Neighborhood Expert promotion it scheduled, along with their campaigns and posts.`,
            };
        } catch (error) {
            console.error("Autopilot disable cleanup failed:", error);
            const deferred =
                axios.isAxiosError(error) && error.response?.status === 409;
            return {
                title: "Auto Pilot is off",
                message: deferred
                    ? "Listing cleanup was deferred while setup finishes — it will complete automatically."
                    : "Listing cleanup hit an error. It will finish automatically within a day.",
                error: true,
            };
        }
    };

    // The single save path: persist the staged settings (switch included), run
    // whatever the on/off transition requires, then notify once.
    const persistStaged = async (nextEnabled: boolean): Promise<void> => {
        if (!staged || !data || saving) return;
        const wasEnabled = data.settings.enabled;
        const turningOn = nextEnabled && !wasEnabled;
        const turningOff = !nextEnabled && wasEnabled;
        // Closing the dialog and raising the loader in one render — the overlay's
        // confirm button does not self-close (same pattern as the listing card
        // delete in ListingDisplay).
        setPendingSave(false);
        setConfirmAction(null);
        setBusyMessage(
            turningOn
                ? "Turning on Auto Pilot — creating campaigns for your listings. This can take a minute."
                : turningOff
                  ? "Turning off Auto Pilot — removing the listings it manages."
                  : "Saving your Auto Pilot settings."
        );
        try {
            const updated = await patchSettings({
                ...staged,
                enabled: nextEnabled,
            });
            if (!updated) {
                // Refused (e.g. 423 while Auto Pilot is still setting up):
                // the switch shows the server's state, and no cleanup runs.
                setStaged({ ...staged, enabled: wasEnabled });
                return;
            }
            setStaged(pickStaged(updated.settings));

            const outcome: SaveOutcome = turningOn
                ? await runReconcile()
                : turningOff
                  ? await runDisableCleanup()
                  : {
                        message: nextEnabled
                            ? "Settings saved. Changes apply to each campaign at its next weekly renewal."
                            : "Settings saved. Auto Pilot is off — you'll manage campaigns manually.",
                    };

            if (outcome.title) {
                setOutcomeModal(outcome);
                return;
            }
            const notify = outcome.error ? toast.error : toast.success;
            notify(
                <CustomToast
                    message={outcome.message}
                    type={outcome.error ? "error" : "success"}
                />,
                { autoClose: 5000 }
            );
        } finally {
            setBusyMessage(null);
        }
    };

    const handleEnableConfirmed = (): Promise<void> => persistStaged(true);

    // "Save & turn on" from the isn't-on-yet modal goes through the same
    // confirmation as the switch: turning on replaces manual campaigns and
    // discards their unpublished posts, and only that dialog says so.
    const requestEnableFromPendingSave = (): void => {
        if (!staged) return;
        setPendingSave(false);
        setStaged({ ...staged, enabled: true });
        setConfirmAction("enable");
    };

    const handleDisableConfirmed = (): Promise<void> => persistStaged(false);

    // Declining the confirm puts the switch back where it was — leaving it
    // flipped would show a state the user just said no to.
    const cancelConfirm = (): void => {
        setConfirmAction(null);
        if (staged && data) {
            setStaged({ ...staged, enabled: data.settings.enabled });
        }
    };

    // Save is the only thing that talks to the server: the switch just stages a
    // value, so turning Autopilot on or off is confirmed and applied here.
    const handleSave = async (): Promise<void> => {
        if (!staged || saving || !data) return;
        const live = data.settings.enabled;
        if (staged.enabled && !live) {
            setConfirmAction("enable");
            return;
        }
        if (!staged.enabled && live) {
            setConfirmAction("disable");
            return;
        }
        // Staying off. A plain success toast here read as "Autopilot is
        // running" — the one thing that had NOT happened. Say so explicitly and
        // offer the turn-on in the same breath.
        if (!staged.enabled) {
            setPendingSave(true);
            return;
        }
        // Autopilot was already on and stays on — persist the settings only.
        await persistStaged(staged.enabled);
    };

    const renderPrereqRow = (
        ok: boolean,
        label: string,
        fixLink?: { to: string; text: string }
    ) => (
        <div className="flex items-center justify-between py-2">
            <div className="flex items-center">
                {ok ? (
                    <Check className="h-5 w-5 text-[#43a047] mr-3" />
                ) : (
                    <X className="h-5 w-5 text-red-500 mr-3" />
                )}
                <span
                    className={`text-sm ${ok ? "text-neutral-600" : "text-neutral-900 font-medium"}`}
                >
                    {label}
                </span>
            </div>
            {!ok && fixLink && (
                <Link
                    to={fixLink.to}
                    className="text-sm text-primary underline"
                >
                    {fixLink.text}
                </Link>
            )}
        </div>
    );

    const pageShell = (children: React.ReactNode) => (
        <Card
            className={`w-full max-w-4xl mx-auto ${
                isMobile ? "h-full m-0 rounded-none" : "h-auto"
            }`}
        >
            <div className="p-4">
                <Button
                    id="back-to-settings-btn"
                    onClick={() => navigate("/settings")}
                    className="mb-4 flex items-center"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                </Button>
            </div>
            {children}
        </Card>
    );

    if (loading || !data || !staged) {
        return pageShell(
            <CardContent className="space-y-4 pb-8">
                <div className="w-full h-6 bg-gray-200 rounded animate-skeleton" />
                <div className="w-2/3 h-6 bg-gray-200 rounded animate-skeleton" />
                <div className="w-1/2 h-6 bg-gray-200 rounded animate-skeleton" />
            </CardContent>
        );
    }

    // LOCKED: plan has no autopilot — pitch + upgrade.
    if (data.autopilotListingLimit <= 0) {
        return pageShell(
            <>
                <CardHeader className="mb-0 pb-0">
                    <CardTitle className="flex items-center">
                        <Rocket className="h-6 w-6 mr-2" /> Auto Pilot
                    </CardTitle>
                    <CardDescription>
                        Hands-free marketing for every listing
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pb-8">
                    <p className="text-sm text-neutral-600">
                        Turn Auto Pilot on once and Marabot does the rest: every
                        listing that syncs from the MLS gets a campaign
                        automatically — posts written, scheduled, and published
                        across your channels. Campaigns pause when a listing
                        goes under contract, celebrate when it sells, and wind
                        down on their own.
                    </p>
                    <p className="text-sm text-neutral-600 font-medium">
                        Auto Pilot is an AI Pro feature — upgrade to unlock it,
                        along with a raised limit of 12 active listings, all
                        marketed automatically.
                    </p>
                    <Button onClick={() => navigate("/settings/subscription")}>
                        Upgrade to AI Pro
                    </Button>
                </CardContent>
            </>
        );
    }

    const prereq = data.prerequisites;
    const settings = data.settings;
    const connectedChannels = ALL_CHANNELS.filter((channel) =>
        data.connectedChannels.includes(channel)
    );
    const unconnectedChannels = ALL_CHANNELS.filter(
        (channel) => !data.connectedChannels.includes(channel)
    );

    return pageShell(
        <>
            <CardHeader className="mb-0 pb-0">
                <CardTitle className="flex items-center">
                    <Rocket className="h-6 w-6 mr-2" /> Auto Pilot
                </CardTitle>
                <CardDescription>
                    Automatic listing campaigns — set it and forget it. Posts
                    publish without approval steps; turning Auto Pilot off
                    returns you to manual campaign management.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pb-8">
                {/* Prerequisites checklist — always visible so the user can
                    see their standing at a glance, all-green included */}
                <div className="border rounded-md p-4 bg-gray-50">
                        <Label className="font-semibold text-primary">
                            {data.eligible
                                ? "Auto Pilot requirements"
                                : "Before you can enable Auto Pilot"}
                        </Label>
                        <div className="mt-2 divide-y">
                            {renderPrereqRow(
                                prereq.mlsApproved,
                                "MLS access approved on your account",
                            )}
                            {renderPrereqRow(
                                prereq.autoIngestEnabled,
                                "Automatic listing sync is on",
                                {
                                    to: "/settings/mls-details",
                                    text: "MLS Agent Profile",
                                }
                            )}
                            {renderPrereqRow(
                                prereq.agentMlsIdSaved,
                                "Your agent MLS ID is saved",
                                {
                                    to: "/settings/mls-details",
                                    text: "MLS Agent Profile",
                                }
                            )}
                            {renderPrereqRow(
                                prereq.socialConnected,
                                "At least one social channel is connected",
                                {
                                    to: "/settings/connections",
                                    text: "Connections",
                                }
                            )}
                            {renderPrereqRow(
                                prereq.accountActive,
                                "Your account is active",
                            )}
                        </div>
                </div>

                <AutopilotSettingsFields
                    staged={staged}
                    onChange={setStaged}
                    connectedChannels={connectedChannels}
                    unconnectedChannels={unconnectedChannels}
                    disabled={saving}
                    enableSwitchDisabled={
                        saving ||
                        (!settings.enabled && !data.eligible) ||
                        autopilotBusy
                    }
                    enableSwitchDisabledReason={
                        autopilotBusy ? AUTOPILOT_OFF_BUSY_TOOLTIP : undefined
                    }
                    // MB-1481: the commercial pricing switch now lives on its
                    // own Settings → Commercial page.
                    hideCommercialSwitch
                />

                <div className="bg-gray-100 p-3 rounded-md">
                    <p className="text-sm text-gray-600">
                        <strong className="text-primary">Note:</strong>{" "}
                        Auto Pilot only markets listings whose MLS status is
                        Active - under-contract and other statuses are skipped.
                        Listings you market for another agent (shared listings)
                        are marketed too, and count toward your Auto Pilot
                        listing limit. Turning Auto Pilot on removes listings you
                        added manually by address before MLS access - your
                        listings come in from your MLS instead. Sold listings
                        are automatically removed 30 days after closing so they
                        stop using your storage. Sold celebration posts go out
                        long before that. Turning Auto Pilot off deletes every
                        listing it manages, shared listings included, along with
                        their campaigns and posts.
                    </p>
                </div>

                <div className="border-t border-gray-100 pt-6 space-y-3">
                    <p className="text-sm text-neutral-500">
                        Saved changes don&apos;t affect posts that are already
                        scheduled - Auto Pilot applies them to each campaign
                        automatically at its next weekly renewal.
                    </p>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className={cn(isMobile && "w-full")}
                    >
                        Save Changes
                    </Button>
                </div>
            </CardContent>

            {busyMessage !== null && (
                <LoadingScreen loadingMessage={busyMessage} />
            )}

            <ReconfirmOverlay
                isOpen={confirmAction !== null}
                onClose={cancelConfirm}
                onConfirm={
                    confirmAction === "enable"
                        ? handleEnableConfirmed
                        : handleDisableConfirmed
                }
                title={
                    confirmAction === "enable"
                        ? "Turn on Auto Pilot"
                        : "Turn off Auto Pilot"
                }
                message={
                    confirmAction === "enable"
                        ? "Auto Pilot will market your active listings and every new listing that syncs in. Posts publish automatically without approval. Listings you added manually by address before MLS access are removed, along with their campaigns and posts - your listings come in from your MLS instead."
                        : "Switching back to manual permanently deletes every listing Auto Pilot manages - including its campaign, all scheduled AND published posts, and media. Deleted listings will not sync back in from your MLS. A Neighborhood Expert promotion that Auto Pilot scheduled is deleted too - the promotion and its campaign are fully removed, so you can set up a fresh one anytime. Your own uploaded promotion images stay in your asset library."
                }
                messageTwo={
                    confirmAction === "enable"
                        ? "Posting starts tomorrow. Eligible manual MLS campaigns and any Neighborhood Expert promotion are replaced by Auto Pilot campaigns right away - their unpublished posts are discarded and regenerated on Auto Pilot's schedule. Published posts are not changed."
                        : "This cannot be undone. Listings without an Auto Pilot campaign are not affected."
                }
                confirmText={confirmAction === "enable" ? "Turn on" : "Turn off"}
                cancelText="Cancel"
            />

            {/* Outcome of turning Autopilot on or off — a toast auto-closed
                before the user could read how many listings had just moved. */}
            <ReconfirmOverlay
                isOpen={outcomeModal !== null}
                onClose={() => setOutcomeModal(null)}
                onConfirm={() => setOutcomeModal(null)}
                noButtons
                title={outcomeModal?.title ?? ""}
                message={outcomeModal?.message}
            >
                <Button
                    className={cn(isMobile && "w-full")}
                    onClick={() => setOutcomeModal(null)}
                >
                    Got it
                </Button>
            </ReconfirmOverlay>

            {/* Save pressed while Autopilot is off. noButtons + children so the
                turn-on stays the primary action — the overlay's own pair styles
                the left button as destructive, which "Save only" is not. */}
            <ReconfirmOverlay
                isOpen={pendingSave}
                onClose={() => setPendingSave(false)}
                onConfirm={() => setPendingSave(false)}
                noButtons
                title="Auto Pilot isn't on yet"
                message={
                    data.eligible
                        ? "Your changes are saved either way. Until Auto Pilot is enabled, you'll manage campaigns manually."
                        : "Your changes will be saved, but Auto Pilot can't be turned on until the requirements above are met."
                }
                messageTwo={
                    data.eligible
                        ? "Turn on Auto Pilot to automatically market your active listings. You'll be asked to confirm first - eligible manual MLS campaigns and any Neighborhood Expert promotion are replaced by Auto Pilot campaigns, and listings added manually before MLS access are removed."
                        : undefined
                }
            >
                <div
                    className={cn(
                        "flex gap-3",
                        isMobile
                            ? "flex-col-reverse w-full"
                            : "flex-row justify-center"
                    )}
                >
                    <Button
                        variant="outv3"
                        className={cn(isMobile && "w-full")}
                        onClick={() => persistStaged(false)}
                    >
                        Save changes only
                    </Button>
                    {data.eligible && (
                        <Button
                            className={cn(isMobile && "w-full")}
                            onClick={requestEnableFromPendingSave}
                        >
                            Save &amp; turn on Auto Pilot
                        </Button>
                    )}
                </div>
            </ReconfirmOverlay>

            <ReconfirmOverlay
                isOpen={leaveOverlay}
                onClose={() => {
                    setLeaveOverlay(false);
                    cancelNavigation();
                }}
                onConfirm={() => {
                    setLeaveOverlay(false);
                    // Programmatic nav (sidebar / Back button) carries a target the hook
                    // can complete; browser back/forward doesn't, so fall back to forceLeave.
                    if (pendingNavigation) {
                        confirmNavigation();
                    } else {
                        setForceLeave(true);
                    }
                }}
                title="Leave without saving?"
                message="You have unsaved changes to your Auto Pilot settings."
                messageTwo="If you leave now, your changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                reverse={true}
            />
        </>
    );
};

export default Autopilot;
